import { TFunction } from 'react-i18next'

import { UserLanguage } from '../../../account/types/User'
import { CatalogueWindow, WINDOW_TO_DAYS } from './catalogueUtils'
import { formatTrendsMonthLabel } from './trendsChartFormat'
import { addMonths, selectedPeriodFor } from './trendsSelectedPeriod'

// One place for the adoption-change hover, so the ranked list's Change column
// and the detail pages' "Adoption change" tile can't describe the same number
// differently. They did until Erno's 2026-09-08 review: the column explained the
// definition, the games behind it and the from → to shares, while the tile
// dropped the shares and reworded the definition.
//
// Takes `t` rather than calling the hook so it stays usable from memoized
// render paths and from the tree's per-row computation (see metricLensCopy).

// The month the Change is measured from, labelled as the adoption chart labels it
// ("Apr 2026"). Momentum compares adoption in the period's last month with adoption in
// its first month, which are the chart's last and first points.
export function changeBaselineLabel(window: CatalogueWindow, language: UserLanguage, today: Date = new Date()): string {
  const period = selectedPeriodFor(WINDOW_TO_DAYS[window], today)
  return period ? formatTrendsMonthLabel(period.startMonth, language) : ''
}

// The period's last complete month, labelled as the adoption chart labels it. The month in
// progress is never part of the period, so it is the month before that one.
export function changeLatestLabel(window: CatalogueWindow, language: UserLanguage, today: Date = new Date()): string {
  const period = selectedPeriodFor(WINDOW_TO_DAYS[window], today)
  return period ? formatTrendsMonthLabel(addMonths(period.inProgressMonth, -1), language) : ''
}

// What the change is about, so the sentence names the right thing.
export type AdoptionChangeSubject = 'blueprint' | 'eventType' | 'eventCategory'

export const CHANGE_SUBJECT_KEY: Record<AdoptionChangeSubject, string> = {
  blueprint: 'market-analytics:change_subject_blueprint_family',
  eventType: 'market-analytics:change_subject_event_type',
  eventCategory: 'market-analytics:change_subject_event_category',
}

export interface AdoptionChangeCopyArgs {
  // Net change in adopting games over the period. Null = unknown.
  deltaGames: number | null
  // The month the change is measured from (`changeBaselineLabel`).
  baselineLabel?: string
  // The period's last month (`changeLatestLabel`).
  latestLabel?: string
  // Defaults to a blueprint family, the level the detail page this copy was written for shows.
  subject?: AdoptionChangeSubject
  // Shown instead of the composed copy when the change is unknown for a reason
  // worth stating rather than merely missing.
  unavailableReason?: string
}

export function adoptionChangeHover(
  { deltaGames, baselineLabel, latestLabel, subject = 'blueprint', unavailableReason }: AdoptionChangeCopyArgs,
  t: TFunction
): string {
  if (deltaGames === null && unavailableReason) return unavailableReason

  // The games line names the first and last month of the period, so the reader can
  // see which two months the percentage-point figure compares.
  let gamesLine: string | undefined
  if (deltaGames !== null && baselineLabel && latestLabel) {
    const options = { count: Math.abs(deltaGames), subject: t(CHANGE_SUBJECT_KEY[subject]), firstMonth: baselineLabel, lastMonth: latestLabel }
    const key = deltaGames > 0 ? 'catalogue_change_games_more' : deltaGames < 0 ? 'catalogue_change_games_fewer' : 'catalogue_change_games_same'
    gamesLine = t(`market-analytics:${key}`, options)
  }

  return [t('market-analytics:tooltip_adoption_change'), gamesLine].filter(Boolean).join(' ')
}
